import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Close, Plus } from "@air/next-icons";
import { Chip } from "@air/primitive-chip";
import { IconButton } from "@air/primitive-icon-button";
import { memo, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { SectionDisplay } from "~/components/AssetModalV2/InfoPanel/DescriptionAndTags/SectionDisplay";
import { PrivateTagsSelect } from "~/components/Tags/PrivateTagsSelect";
import { SELECTED_CHIP } from "~/constants/testIDs";
import { centralizedClipIdSelector, centralizedClipSelector, centralizedClipTagsSelector } from "~/store/centralizedClip/selectors";
import { useCreateNewTagForAsset, useUpdateClipTags } from "~/swr-hooks/clips/useUpdateClipTags";
import { canEditClipTags, canRemoveClipTags } from "~/utils/permissions/assetPermissions";
import { canCreateTag } from "~/utils/permissions/workspacePermissions";
import { useAirSelector, useAirStore } from "~/utils/ReduxUtils";
export const AssetTagsList = /*#__PURE__*/ memo((param)=>{
    let { permissions } = param;
    const [isEditingTags, setIsEditingTags] = useState(false);
    const tags = useAirSelector(centralizedClipTagsSelector);
    const selectedTags = useMemo(()=>tags !== null && tags !== void 0 ? tags : [], [
        tags
    ]);
    const clipId = useSelector(centralizedClipIdSelector); // use id from centralized id to match current selected version
    const store = useAirStore();
    const { updateClipTags } = useUpdateClipTags();
    const { createNewTagForAsset } = useCreateNewTagForAsset();
    const canEditTags = canEditClipTags(permissions) && canRemoveClipTags(permissions);
    const canCreateTags = canCreateTag(permissions);
    const onSelectionChange = useCallback((tags)=>{
        const clip = centralizedClipSelector(store.getState());
        const selectedTags = tags.map((tag)=>({
                ...tag,
                isDemo: false
            }));
        updateClipTags.mutate({
            tags: selectedTags,
            clip
        });
    }, [
        store,
        updateClipTags
    ]);
    const [debouncedOnSelectionChange] = useDebouncedCallback(onSelectionChange, 500);
    const onCreate = useCallback(async (label)=>{
        if (clipId) {
            await createNewTagForAsset({
                clipId,
                tag: {
                    label
                },
                trackLocation: "asset-details"
            });
        }
    }, [
        clipId,
        createNewTagForAsset
    ]);
    const handleRemoveTag = useCallback((tagToRemove)=>{
        const clip = centralizedClipSelector(store.getState());
        const newTags = selectedTags.filter((t)=>t.id !== tagToRemove.id);
        updateClipTags.mutate({
            tags: newTags,
            clip
        });
    }, [
        selectedTags,
        updateClipTags,
        store
    ]);
    const onAddTagClicked = useCallback(()=>{
        setIsEditingTags((prev)=>!prev);
    }, []);
    return /*#__PURE__*/ _jsxs(SectionDisplay, {
        title: "Tags",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex flex-wrap gap-2",
                children: selectedTags === null || selectedTags === void 0 ? void 0 : selectedTags.map((tag)=>/*#__PURE__*/ _jsxs(Chip, {
                        appearance: "outline",
                        shape: "rounded",
                        size: "medium",
                        "data-testid": SELECTED_CHIP,
                        "data-tag": tag.label,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex items-center",
                                children: tag.label
                            }),
                            isEditingTags && /*#__PURE__*/ _jsx(IconButton, {
                                appearance: "ghost",
                                color: "grey",
                                size: "medium",
                                icon: Close,
                                label: "Remove ".concat(tag.label),
                                onClick: ()=>handleRemoveTag(tag)
                            })
                        ]
                    }, tag.id))
            }),
            canEditTags && (isEditingTags ? /*#__PURE__*/ _jsxs("div", {
                className: "relative w-full px-2 py-3",
                children: [
                    /*#__PURE__*/ _jsx(PrivateTagsSelect, {
                        className: "ml-1 grow",
                        placeholder: "Add tags",
                        sortBy: "recentlyUsed",
                        onCreateTag: canCreateTags ? onCreate : undefined,
                        onSelectedTagsChanged: debouncedOnSelectionChange,
                        selectedTags: selectedTags,
                        disabled: !canEditTags,
                        hideChipsInInput: true
                    }),
                    /*#__PURE__*/ _jsx(IconButton, {
                        appearance: "ghost",
                        color: "grey",
                        icon: Close,
                        label: "Remove all tags",
                        onClick: ()=>setIsEditingTags(false),
                        className: "absolute right-2 top-1/2 z-20 -translate-y-1/2",
                        size: "medium"
                    })
                ]
            }) : /*#__PURE__*/ _jsx("div", {
                children: /*#__PURE__*/ _jsx(Chip, {
                    appearance: "outline",
                    asChild: true,
                    shape: "rounded",
                    size: "medium",
                    children: /*#__PURE__*/ _jsxs("button", {
                        onClick: onAddTagClicked,
                        children: [
                            /*#__PURE__*/ _jsx(Plus, {
                                className: "size-3"
                            }),
                            "Add"
                        ]
                    })
                })
            }))
        ]
    });
});
AssetTagsList.displayName = "AssetTagsList";
